import { AnnotationResource } from '../resources/annotation.resource';
import { AnnotationRelationResource } from '../resources/annotation-relation.resource';

export type CharDiffSegment = {
  type: 'equal' | 'insert' | 'delete';
  text: string;
};

/**
 * Character-level diff between two strings using a simple LCS approach.
 * Returns segments tagged as equal/insert/delete.
 */
export function charDiff(oldStr: string, newStr: string): CharDiffSegment[] {
  const m = oldStr.length;
  const n = newStr.length;

  // Build LCS table
  const dp: number[][] = [];
  for (let i = 0; i <= m; i++) {
    dp[i] = [];
    for (let j = 0; j <= n; j++) {
      if (i === 0 || j === 0) {
        dp[i][j] = 0;
      } else if (oldStr[i - 1] === newStr[j - 1]) {
        dp[i][j] = dp[i - 1][j - 1] + 1;
      } else {
        dp[i][j] = Math.max(dp[i - 1][j], dp[i][j - 1]);
      }
    }
  }

  // Backtrack to build diff segments
  const raw: CharDiffSegment[] = [];
  let i = m;
  let j = n;
  while (i > 0 || j > 0) {
    if (i > 0 && j > 0 && oldStr[i - 1] === newStr[j - 1]) {
      raw.push({ type: 'equal', text: oldStr[i - 1] });
      i--;
      j--;
    } else if (j > 0 && (i === 0 || dp[i][j - 1] >= dp[i - 1][j])) {
      raw.push({ type: 'insert', text: newStr[j - 1] });
      j--;
    } else {
      raw.push({ type: 'delete', text: oldStr[i - 1] });
      i--;
    }
  }
  raw.reverse();

  // Merge consecutive segments of the same type
  const merged: CharDiffSegment[] = [];
  for (const seg of raw) {
    const last = merged[merged.length - 1];
    if (last && last.type === seg.type) {
      last.text += seg.text;
    } else {
      merged.push({ ...seg });
    }
  }
  return merged;
}

export type DiffStatus = 'added' | 'removed' | 'changed' | 'unchanged';

export type AnnotationDiffEntry = {
  status: DiffStatus;
  oldValue?: string;
  newValue?: string;
  oldAnnotation?: AnnotationResource;
};

export type AnnotationDiffResult = {
  entries: { [annotationId: string]: AnnotationDiffEntry };
  removedAnnotations: AnnotationResource[];
  summary: {
    added: number;
    removed: number;
    changed: number;
    unchanged: number;
  };
};

type AnnotationKey = string;
type ParentOffsets = { start?: number; end?: number } | undefined;
type ParentLookup = Map<number, ParentOffsets>;

function buildParentLookup(
  annotations: AnnotationResource[],
  relations: AnnotationRelationResource[],
): ParentLookup {
  const byId = new Map<number, AnnotationResource>();
  for (const a of annotations) {
    if (a.attributeAnnotationId != null) {
      byId.set(a.attributeAnnotationId, a);
    }
  }
  const lookup = new Map<number, ParentOffsets>();
  for (const rel of relations) {
    if (rel.relationType === 'child-of') {
      const parent = byId.get(rel.rightAttributeAnnotationId);
      if (parent) {
        lookup.set(rel.leftAttributeAnnotationId, {
          start: parent.startOffset,
          end: parent.endOffset,
        });
      }
    }
  }
  return lookup;
}

function parentSuffix(
  a: AnnotationResource,
  parentLookup: ParentLookup,
): string {
  if (a.attributeAnnotationId == null) return '';
  const p = parentLookup.get(a.attributeAnnotationId);
  if (!p) return '';
  return `|p:${p.start ?? ''}:${p.end ?? ''}`;
}

function makeKey(
  a: AnnotationResource,
  parentLookup: ParentLookup,
): AnnotationKey {
  const ps = parentSuffix(a, parentLookup);
  if (a.isMetadata || (a.startOffset == null && a.endOffset == null)) {
    return `meta:${a.annotationType}:${a.value}${ps}`;
  }
  return `text:${a.annotationType}:${a.startOffset}:${a.endOffset}:${a.value}${ps}`;
}

function makeMatchKey(a: AnnotationResource): AnnotationKey {
  if (a.isMetadata || (a.startOffset == null && a.endOffset == null)) {
    return `meta:${a.annotationType}`;
  }
  return `text:${a.annotationType}:${a.startOffset}:${a.endOffset}`;
}

export const AnnotationDiff = {
  compute(
    olderAnnotations: AnnotationResource[],
    newerAnnotations: AnnotationResource[],
    olderRelations: AnnotationRelationResource[] = [],
    newerRelations: AnnotationRelationResource[] = [],
  ): AnnotationDiffResult {
    const summary = { added: 0, removed: 0, changed: 0, unchanged: 0 };
    const entries: { [id: string]: AnnotationDiffEntry } = {};
    const removedAnnotations: AnnotationResource[] = [];

    const olderParents = buildParentLookup(olderAnnotations, olderRelations);
    const newerParents = buildParentLookup(newerAnnotations, newerRelations);

    // Build lookup maps for older annotations
    const olderByExactKey = new Map<AnnotationKey, AnnotationResource[]>();
    const olderByMatchKey = new Map<AnnotationKey, AnnotationResource[]>();

    for (const a of olderAnnotations) {
      const exactKey = makeKey(a, olderParents);
      const matchKey = makeMatchKey(a);
      if (!olderByExactKey.has(exactKey)) olderByExactKey.set(exactKey, []);
      olderByExactKey.get(exactKey)!.push(a);
      if (!olderByMatchKey.has(matchKey)) olderByMatchKey.set(matchKey, []);
      olderByMatchKey.get(matchKey)!.push(a);
    }

    const matchedOlderIds = new Set<number>();

    for (const newAnn of newerAnnotations) {
      const id = String(newAnn.attributeAnnotationId || newAnn.clientId || '');
      const exactKey = makeKey(newAnn, newerParents);
      const matchKey = makeMatchKey(newAnn);

      // Try exact match first (same type, offsets, and value)
      const exactMatches = olderByExactKey.get(exactKey) || [];
      const unmatchedExact = exactMatches.find(
        a => !matchedOlderIds.has(a.attributeAnnotationId!),
      );

      if (unmatchedExact) {
        matchedOlderIds.add(unmatchedExact.attributeAnnotationId!);
        entries[id] = { status: 'unchanged' };
        summary.unchanged++;
        continue;
      }

      // Try fuzzy match (same type and offsets, different value)
      const fuzzyMatches = olderByMatchKey.get(matchKey) || [];
      const unmatchedFuzzy = fuzzyMatches.find(
        a => !matchedOlderIds.has(a.attributeAnnotationId!),
      );

      if (unmatchedFuzzy) {
        matchedOlderIds.add(unmatchedFuzzy.attributeAnnotationId!);
        entries[id] = {
          status: 'changed',
          oldValue: unmatchedFuzzy.value,
          newValue: newAnn.value,
          oldAnnotation: unmatchedFuzzy,
        };
        summary.changed++;
        continue;
      }

      // No match — this annotation was added
      entries[id] = { status: 'added', newValue: newAnn.value };
      summary.added++;
    }

    // Any unmatched older annotations are removed
    for (const oldAnn of olderAnnotations) {
      if (!matchedOlderIds.has(oldAnn.attributeAnnotationId!)) {
        removedAnnotations.push(oldAnn);
        summary.removed++;
      }
    }

    return { entries, removedAnnotations, summary };
  },
};
